import cn from "classnames";
import {
  Dialog,
  Loader,
  Overlay,
  useMediaQuery,
  NotificationsProvider,
} from "@rshbintech.dboul/core-ui";
import { useEffect } from "react";
import { useToggle } from "../hooks";
import { FeedbackFormProps } from "./types";
import { FeedbackFormContent } from "./feedback-form-content";
import { useFeedbackForm, useFeedbackFormState } from "./hooks";
import {
  ButtonElement,
  Schema,
  TitleCSI,
} from "./components-from-schema/types";
import { FooterCsi } from "./footer-csi/footer-csi";
import { ErrorDialog } from "./error-dialog";
import styles from "./styles.scss";

const FeedbackFormWithoutNotificationProvider = ({
  formKey,
  onSubmit,
  onClose,
}: FeedbackFormProps) => {
  const isMobile = useMediaQuery("mobile");
  const {
    isOpen: isFeedbackFormOpen,
    handleOpen: handleFeedbackFormOpen,
    handleClose: handleFeedbackFormClose,
  } = useToggle(false);

  const {
    data,
    isLoading,
    hasError,
    resetError,
    updateIsClosedFlag,
    sendFeedback,
  } = useFeedbackFormState(formKey);

  const { form, onChange, isFormCompleted } = useFeedbackForm(
    formKey,
    data?.schema
  );

  useEffect(() => {
    if (!data) {
      return;
    }

    handleFeedbackFormOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleSubmit = async () => {
    try {
      await sendFeedback(form);
      handleFeedbackFormClose();
      onSubmit?.();
    } catch (error) {}
  };

  const handleClose = () => {
    updateIsClosedFlag();

    handleFeedbackFormClose();
    onClose?.();
  };

  if (!data) {
    return null;
  }

  const { type, schema } = data;

  if (!type || !schema) {
    return null;
  }

  const parsedSchema: Schema = JSON.parse(schema);
  const isCsiForm = type === "CSI";
  const isNpsForm = type === "NPS";
  const titleCsi =
    isCsiForm &&
    (parsedSchema.find((item) => item.component === "title") as TitleCSI)
      .children;

  const buttonTextCsi =
    isCsiForm &&
    (parsedSchema.find((item) => item.component === "button") as ButtonElement)
      .children;

  return (
    <Dialog
      title={titleCsi}
      className={cn(styles["feedback-dialog"], styles[type.toLowerCase()])}
      fullscreen={isCsiForm}
      size={isNpsForm ? "medium" : "small"}
      opened={isFeedbackFormOpen}
      onClose={handleClose}
      footer={
        !isMobile ? (
          <FooterCsi
            submitButtonText={buttonTextCsi}
            onSubmit={handleSubmit}
            onReject={handleClose}
            isSubmitDisabled={!isFormCompleted}
          />
        ) : null
      }
    >
      <>
        <FeedbackFormContent
          type={type}
          data={parsedSchema}
          form={form}
          onChange={onChange}
          onSubmit={handleSubmit}
          isSubmitDisabled={!isFormCompleted}
        />
        <Overlay
          className={styles["loader-overlay"]}
          transitionDuration={0}
          opened={isLoading}
        >
          <Loader />
        </Overlay>

        <ErrorDialog
          isOpen={hasError}
          isLoading={isLoading}
          onBack={resetError}
          onRetry={handleSubmit}
        />
      </>
    </Dialog>
  );
};

export const FeedbackForm = ({ ...props }: FeedbackFormProps) => (
  <NotificationsProvider>
    <FeedbackFormWithoutNotificationProvider {...props} />
  </NotificationsProvider>
);
